// Pure helpers for the debt reconciliation editor. Kept free of Svelte/DOM so
// the allocation, role and validation rules can be unit-tested directly and
// mirror the authoritative Go logic in cmd/api/debts.go.

import { round2 } from './split';
import { calculateCategorySummary, DEBT_TYPES, DEBT_CLASSES } from './db';
import type {
	Allocation,
	UnpaidCharge,
	Transaction,
	CategorySummary,
	SeriesPoint,
	PaymentView,
	DebtClass,
	DebtView,
} from './db';

function chargeKey(pk: string, sk: string): string {
	return `${pk} ${sk}`;
}

// Debt classes in display order (Monitored last — it's de-emphasised).
export const DEBT_CLASS_ORDER: DebtClass[] = [DEBT_CLASSES.CONSUMER, DEBT_CLASSES.ASSET_BACKED, DEBT_CLASSES.MONITORED];
export const DEBT_CLASS_LABELS: Record<DebtClass, string> = {
	consumer: 'Consumer',
	asset_backed: 'Asset-backed',
	monitored: 'Monitored',
};

// cardCoherence compares the live linked-account balance against what the
// ledger expects (legacy remaining + unpaid new charges). A non-null result
// means the two have drifted beyond rounding — usually unreconciled payments.
export function cardCoherence(debt: DebtView): {
	expected: number;
	actual: number;
	gap: number;
} | null {
	if (debt.debtType !== DEBT_TYPES.CREDIT_CARD) return null;
	const raw = debt.linkedAccountBalance;
	if (raw === undefined || raw === 0) return null;
	const actual = Math.abs(raw);
	// Prefer the always-present scalar (the slim list view the dashboard uses
	// omits the unpaidCharges array — summing it there would understate
	// expected by the unpaid total and report phantom drift). Fall back to the
	// array for any payload without the scalar.
	const unpaidNewCharges =
		debt.unpaidChargesTotal ?? (debt.unpaidCharges ?? []).reduce((s, c) => s + (c.unpaid ?? 0), 0);
	const expected = round2((debt.legacyRemaining ?? 0) + unpaidNewCharges);
	const gap = round2(expected - actual);
	if (Math.abs(gap) <= 1) return null;
	return { expected, actual: round2(actual), gap };
}

// DebtSummary is the dashboard's per-class roll-up of where the user's debts
// sit today, what's been paid this month, and any guards that need attention.
// Pure derivation from a DebtView[] (the list endpoint shape) plus a
// CategorySummary[] (the dashboard already computes one via
// `computeMonthlyBudget`) — no I/O — so it's safe to evaluate in a $derived.
export interface DebtSummaryClass {
	total: number; // sum of "what we owe" for the class
	paidThisMonth: number; // sum of |summary.total| over the class's servicing categories
	debts: DebtView[];
}

export interface MortgageSummary extends DebtSummaryClass {
	equity: number;
	equityPct: number; // weighted by valuation
	lvrPct: number; // weighted by valuation
}

export interface DebtAlerts {
	dueFacilities: DebtView[];
	overdueFacilities: DebtView[];
	coherenceDrifts: { debt: DebtView; gap: number }[];
	total: number;
}

export interface DebtSummary {
	consumer: DebtSummaryClass;
	assetBacked: MortgageSummary;
	// Monitored debts (any type, debtClass === 'monitored') get their own
	// summary tile. No alerts fire for them — they're "watch, not act".
	monitored: DebtSummaryClass;
	alerts: DebtAlerts;
	// Union of legacyCategory + interestCategory + paymentCategory across every
	// debt. The dashboard uses it to tag the budget tiles whose category is
	// "this is debt servicing" so they read as distinct from discretionary spend.
	servicingCategories: ReadonlySet<string>;
}

// paymentSourceChip returns the small dashboard chip text when a debt is paid
// at source (PAYE) rather than from the user's bank account. Returns null
// when no chip is needed.
export function paymentSourceChip(debt: DebtView): string | null {
	return debt.paymentSource === 'payroll' ? 'PAYE' : null;
}

function debtCategoryNames(debt: DebtView): string[] {
	const out: string[] = [];
	for (const c of [debt.legacyCategory, debt.interestCategory, debt.paymentCategory]) {
		const trimmed = (c ?? '').trim();
		if (trimmed) out.push(trimmed);
	}
	return out;
}

// classOf returns a debt's class, defaulting unset rows by type the same way
// Go's effectiveDebtClass does — mortgage → asset_backed, otherwise consumer.
// The API resolves this server-side via buildDebtView, so DebtViews coming
// out of the list endpoint always carry a debtClass. The defaulting here is
// belt-and-braces for any local construction (tests, fixtures) so the SPA
// can't disagree with the API on a class-less mortgage.
export function classOf(debt: { debtType?: string; debtClass?: DebtClass }): DebtClass {
	if (debt.debtClass) return debt.debtClass;
	if (debt.debtType === DEBT_TYPES.MORTGAGE) return DEBT_CLASSES.ASSET_BACKED;
	return DEBT_CLASSES.CONSUMER;
}

// summariseDebts aggregates the list-endpoint DebtView[] into the shape the
// dashboard renders: per-class totals (consumer + asset-backed), this month's
// servicing spend, alert chips (facility guard + card coherence drift), and
// the set of categories that count as debt-servicing for tile tagging.
// owedFor returns the "what we owe" magnitude for a single debt, choosing
// the right field per type. Used by every class bucket so a credit card,
// facility, or mortgage classified as `monitored` shows the same headline
// number it would under its natural class.
function owedFor(d: DebtView): number {
	if (d.debtType === DEBT_TYPES.CREDIT_CARD) {
		const fromBalance = d.linkedAccountBalance ?? 0;
		return fromBalance !== 0 ? Math.abs(fromBalance) : (d.legacyRemaining ?? 0);
	}
	if (d.debtType === DEBT_TYPES.INTEREST_FREE_FACILITY) {
		return d.currentBalance ?? 0;
	}
	if (d.debtType === DEBT_TYPES.MORTGAGE) {
		return d.totalBalance ?? 0;
	}
	return 0;
}

export function summariseDebts(debts: DebtView[], summary: CategorySummary[]): DebtSummary {
	const consumer: DebtSummaryClass = { total: 0, paidThisMonth: 0, debts: [] };
	const assetBacked: MortgageSummary = {
		total: 0,
		paidThisMonth: 0,
		debts: [],
		equity: 0,
		equityPct: 0,
		lvrPct: 0,
	};
	const monitored: DebtSummaryClass = { total: 0, paidThisMonth: 0, debts: [] };
	const alerts: DebtAlerts = {
		dueFacilities: [],
		overdueFacilities: [],
		coherenceDrifts: [],
		total: 0,
	};

	const servicingCategories = new Set<string>();
	// Per-category → which class "owns" it (for paidThisMonth attribution).
	const ownerOf = new Map<string, DebtClass>();
	let weightedEquityPct = 0;
	let weightedLvrPct = 0;
	let valuationTotal = 0;

	for (const d of debts) {
		const cls = classOf(d);
		for (const name of debtCategoryNames(d)) {
			servicingCategories.add(name);
			if (!ownerOf.has(name)) ownerOf.set(name, cls);
		}

		const owed = owedFor(d);

		if (cls === DEBT_CLASSES.MONITORED) {
			// Monitored is "watch, not act": same per-type balance, but no
			// alerts and no equity rollup (mortgages monitored elsewhere — e.g.
			// a manually-tracked rental — still show their balance here but
			// not in the asset-backed equity tile).
			monitored.total = round2(monitored.total + owed);
			monitored.debts.push(d);
			continue;
		}

		if (cls === DEBT_CLASSES.ASSET_BACKED) {
			assetBacked.total = round2(assetBacked.total + owed);
			assetBacked.debts.push(d);
			if (d.debtType === DEBT_TYPES.MORTGAGE) {
				assetBacked.equity = round2(assetBacked.equity + (d.equity ?? 0));
				const valuation = d.houseValuation ?? 0;
				if (valuation > 0) {
					weightedEquityPct += (d.equityPct ?? 0) * valuation;
					weightedLvrPct += (d.lvrPct ?? 0) * valuation;
					valuationTotal += valuation;
				}
			}
			continue;
		}

		// Default class: consumer — runs the alert checks per type.
		consumer.total = round2(consumer.total + owed);
		consumer.debts.push(d);
		if (d.debtType === DEBT_TYPES.CREDIT_CARD) {
			const drift = cardCoherence(d);
			if (drift) alerts.coherenceDrifts.push({ debt: d, gap: drift.gap });
		} else if (d.debtType === DEBT_TYPES.INTEREST_FREE_FACILITY) {
			if (d.guardStatus === 'due') alerts.dueFacilities.push(d);
			else if (d.guardStatus === 'overdue') alerts.overdueFacilities.push(d);
		}
	}

	if (valuationTotal > 0) {
		assetBacked.equityPct = round2(weightedEquityPct / valuationTotal);
		assetBacked.lvrPct = round2(weightedLvrPct / valuationTotal);
	}

	for (const s of summary) {
		const cls = ownerOf.get(s.category);
		if (!cls) continue;
		// Only net-expense categories count toward "paid this month".
		const spend = s.total < 0 ? Math.abs(s.total) : 0;
		if (spend === 0) continue;
		if (cls === DEBT_CLASSES.CONSUMER) consumer.paidThisMonth = round2(consumer.paidThisMonth + spend);
		else if (cls === DEBT_CLASSES.ASSET_BACKED)
			assetBacked.paidThisMonth = round2(assetBacked.paidThisMonth + spend);
		else if (cls === DEBT_CLASSES.MONITORED)
			monitored.paidThisMonth = round2(monitored.paidThisMonth + spend);
	}

	alerts.total =
		alerts.dueFacilities.length + alerts.overdueFacilities.length + alerts.coherenceDrifts.length;

	return { consumer, assetBacked, monitored, alerts, servicingCategories };
}

// groupDebtsByClass partitions debts into ordered, non-empty class groups
// (consumer → asset_backed → monitored), using classOf so a class-less
// mortgage lands under asset_backed (matching the server-side default).
export function groupDebtsByClass(
	debts: DebtView[]
): { class: DebtClass; label: string; debts: DebtView[] }[] {
	return DEBT_CLASS_ORDER.map((c) => ({
		class: c,
		label: DEBT_CLASS_LABELS[c],
		debts: debts.filter((d) => classOf(d) === c),
	})).filter((g) => g.debts.length > 0);
}

// Legacy balance still owing: the frozen start minus all recognised legacy
// paydown, clamped at zero (it only ever decreases).
export function legacyRemaining(start: number, legacyAllocated: number): number {
	return Math.max(0, round2((start || 0) - (legacyAllocated || 0)));
}

export function isLegacyActive(remaining: number): boolean {
	return remaining > 0.005;
}

// paidDownPercent returns the integer % of `start` that has been paid off given
// the amount `remaining`. Clamped to [0, 100]; 0 when `start` is non-positive.
// Shared by the credit-card legacy bar (start = legacyBalanceStart, remaining =
// legacyRemaining) and the facility bar (start = originalPrincipal, remaining =
// currentBalance) so both surfaces compute the same way.
export function paidDownPercent(start: number, remaining: number): number {
	if (!(start > 0)) return 0;
	const pct = ((start - remaining) / start) * 100;
	return Math.max(0, Math.min(100, Math.round(pct)));
}

export function allocationsTotal(allocations: Allocation[]): number {
	return round2(allocations.reduce((s, a) => s + (Number.isFinite(a.amount) ? a.amount : 0), 0));
}

export function sumByKind(allocations: Allocation[], kind: Allocation['kind']): number {
	return round2(
		allocations.filter((a) => a.kind === kind).reduce((s, a) => s + (a.amount || 0), 0)
	);
}

// suggestAllocations applies "clear oldest unpaid charges first, remainder to
// legacy (capped), any excess is overpayment" to a payment magnitude. The
// unpaid pool is assumed oldest-first (as the API returns it). A payment that
// exactly matches one charge's unpaid amount is allocated to that charge in
// full instead (oldest wins if several match).
export function suggestAllocations(
	magnitude: number,
	unpaidPool: UnpaidCharge[],
	legacyRem: number
): Allocation[] {
	let remaining = round2(magnitude);
	if (remaining > 0.005) {
		for (const c of unpaidPool) {
			if (Math.abs(c.unpaid - remaining) <= 0.005) {
				return [{ kind: 'purchase', targetPk: c.pk, targetSk: c.sk, amount: remaining }];
			}
		}
	}
	const allocs: Allocation[] = [];
	for (const c of unpaidPool) {
		if (remaining <= 0.005) break;
		const take = round2(Math.min(remaining, c.unpaid));
		if (take <= 0.005) continue;
		allocs.push({ kind: 'purchase', targetPk: c.pk, targetSk: c.sk, amount: take });
		remaining = round2(remaining - take);
	}
	if (remaining > 0.005 && legacyRem > 0.005) {
		const take = round2(Math.min(remaining, legacyRem));
		allocs.push({ kind: 'legacy', amount: take });
		remaining = round2(remaining - take);
	}
	if (remaining > 0.005) {
		allocs.push({ kind: 'overpayment', amount: round2(remaining) });
	}
	return allocs;
}

export interface RoleSplit {
	role: 'minimum' | 'extra' | 'mixed';
	minimumPortion: number;
	extraPortion: number;
}

// inferRole splits a payment magnitude into the required minimum and the extra
// paydown using the configured minimum payment.
export function inferRole(magnitude: number, minimumPayment: number): RoleSplit {
	if (!minimumPayment || minimumPayment <= 0) {
		return { role: 'extra', minimumPortion: 0, extraPortion: round2(magnitude) };
	}
	const minimumPortion = round2(Math.min(magnitude, minimumPayment));
	const extraPortion = round2(magnitude - minimumPortion);
	if (extraPortion <= 0.005) return { role: 'minimum', minimumPortion, extraPortion: 0 };
	if (minimumPortion <= 0.005) return { role: 'extra', minimumPortion: 0, extraPortion };
	return { role: 'mixed', minimumPortion, extraPortion };
}

// validateAllocations returns null when the allocations are a valid reconciliation
// of a payment, otherwise a short human-readable reason. Mirrors the server check.
export function validateAllocations(
	magnitude: number,
	allocations: Allocation[],
	unpaidPool: UnpaidCharge[],
	legacyRem: number,
	legacyCategory: string
): string | null {
	if (allocations.length === 0) return 'Add at least one allocation';
	const avail = new Map<string, number>();
	for (const c of unpaidPool) avail.set(chargeKey(c.pk, c.sk), c.unpaid);

	const reqByKey = new Map<string, number>();
	let sum = 0;
	let legacySum = 0;
	for (const al of allocations) {
		if (!(al.amount > 0)) return 'Allocation amounts must be positive';
		if (al.kind === 'purchase') {
			const k = chargeKey(al.targetPk ?? '', al.targetSk ?? '');
			if (!avail.has(k)) return 'Allocation targets an unknown or fully-paid charge';
			reqByKey.set(k, (reqByKey.get(k) ?? 0) + al.amount);
		} else if (al.kind === 'legacy') {
			legacySum += al.amount;
		} else if (al.kind !== 'overpayment') {
			return 'Invalid allocation kind';
		}
		sum += al.amount;
	}
	for (const [k, req] of reqByKey) {
		if (req > (avail.get(k) ?? 0) + 0.005) return "Allocation exceeds the charge's unpaid amount";
	}
	if (legacySum > legacyRem + 0.005) return 'Legacy allocation exceeds remaining legacy balance';
	if (Math.abs(sum - magnitude) > 0.01) return 'Allocations must sum to the payment amount';
	if (legacySum > 0.005 && legacyCategory.trim() === '')
		return 'Debt has no legacy category configured';
	return null;
}

// A read-only summary of one card account's own activity for a pay cycle.
// Amounts follow the ledger sign convention (spend is negative).
export interface CardActivity {
	totalSpend: number; // sum of charge categories (negative)
	interestTotal: number; // spend in the debt's interest category (negative, or 0)
	creditsIn: number; // positive amounts landing on the card (payments/refunds), positive
	count: number; // number of charge lines contributing to spend
	byCategory: CategorySummary[]; // spend grouped by category, largest spend first
}

// cardActivity summarises the transactions on a debt's card account for the cycle
// they're passed in. Grounded on account_id; spend grouping reuses
// calculateCategorySummary (which skips transfers/excluded and honours splits).
export function cardActivity(
	transactions: Transaction[],
	debt: { cardAccountId?: string; interestCategory?: string }
): CardActivity {
	const accountId = debt.cardAccountId ?? '';
	const empty: CardActivity = { totalSpend: 0, interestTotal: 0, creditsIn: 0, count: 0, byCategory: [] };
	if (!accountId) return empty;

	const cardTx = transactions.filter((t) => t.accountId === accountId);
	if (cardTx.length === 0) return empty;

	// Spend by category: only the charge (negative) categories. Positive credits and
	// own-account transfers are already dropped by calculateCategorySummary.
	const byCategory = calculateCategorySummary(cardTx).filter((s) => s.total < 0);

	let totalSpend = 0;
	let interestTotal = 0;
	let count = 0;
	for (const s of byCategory) {
		totalSpend = round2(totalSpend + s.total);
		count += s.count;
		if (debt.interestCategory && s.category === debt.interestCategory) {
			interestTotal = round2(interestTotal + s.total);
		}
	}

	const creditsIn = round2(
		cardTx.filter((t) => (t.amount ?? 0) > 0).reduce((sum, t) => sum + t.amount, 0)
	);

	return { totalSpend, interestTotal, creditsIn, count, byCategory };
}

// ---- Facility payoff projection (0% → linear) ----

function monthsBetweenDates(from: Date, to: Date): number {
	let m = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
	if (to.getDate() < from.getDate()) m--;
	return m < 0 ? 0 : m;
}

function daysBetweenDates(from: Date, to: Date): number {
	const d = Math.floor((to.getTime() - from.getTime()) / 86_400_000);
	return d < 0 ? 0 : d;
}

// periodsBetween counts whole payment periods between two dates (mirrors the
// server's periodsElapsed): monthly = whole months, four-weekly = ⌊days/28⌋,
// fortnightly = ⌊days/14⌋, weekly = ⌊days/7⌋.
export function periodsBetween(from: Date, to: Date, frequency: string): number {
	if (to < from) return 0;
	switch (frequency) {
		case 'four-weekly':
			return Math.floor(daysBetweenDates(from, to) / 28);
		case 'fortnightly':
			return Math.floor(daysBetweenDates(from, to) / 14);
		case 'weekly':
			return Math.floor(daysBetweenDates(from, to) / 7);
		case 'monthly':
		default:
			return monthsBetweenDates(from, to);
	}
}

// lastPaymentMagnitude returns the absolute amount of the most recent payment, or 0.
export function lastPaymentMagnitude(payments: PaymentView[] | undefined): number {
	if (!payments || payments.length === 0) return 0;
	const sorted = [...payments].sort((a, b) => a.date.localeCompare(b.date));
	return Math.abs(sorted[sorted.length - 1].amount ?? 0);
}

export interface PayoffProjection {
	series: SeriesPoint[]; // monthly {month, value} from `from` to payoff
	payoffMonth: string | null; // YYYY-MM the balance reaches target, or null if never
	months: number; // calendar months from `from` to payoff
}

function monthKeyParts(year: number, monthIndex0: number): string {
	return `${year}-${String(monthIndex0 + 1).padStart(2, '0')}`;
}

// projectPayoff walks forward month by month, reducing the balance by
// paymentPerPeriod each period until it reaches the target (0% → linear). The
// first point is the current month at startBalance, so it joins the actual line.
export function projectPayoff(
	startBalance: number,
	paymentPerPeriod: number,
	frequency: string,
	from: Date,
	target = 0,
	capMonths = 600
): PayoffProjection {
	const start = round2(startBalance);
	const fromMonth = monthKeyParts(from.getFullYear(), from.getMonth());
	if (start <= target) {
		return { series: [{ month: fromMonth, value: round2(target) }], payoffMonth: fromMonth, months: 0 };
	}
	if (!(paymentPerPeriod > 0)) {
		return { series: [{ month: fromMonth, value: start }], payoffMonth: null, months: 0 };
	}

	const series: SeriesPoint[] = [];
	let year = from.getFullYear();
	let monthIndex = from.getMonth();
	for (let i = 0; i <= capMonths; i++) {
		const monthEnd = new Date(year, monthIndex + 1, 0);
		const periods = periodsBetween(from, monthEnd, frequency);
		let bal = round2(start - paymentPerPeriod * periods);
		if (bal < target) bal = target;
		const mk = monthKeyParts(year, monthIndex);
		series.push({ month: mk, value: bal });
		if (bal <= target) {
			return { series, payoffMonth: mk, months: i };
		}
		monthIndex++;
		if (monthIndex > 11) {
			monthIndex = 0;
			year++;
		}
	}
	return { series, payoffMonth: null, months: capMonths };
}
